import { useCallback, useState } from 'react';

export interface ChatPromptSenderArgs {
  prompt: string;
  rawPrompt: string;
  file: File | null;
}

export interface ChatPromptPayload {
  prompt: string;
  displayContent: string;
}

export interface UseChatPromptSenderOptions {
  getPrompt: () => string;
  getRawPrompt?: () => string;
  getAttachment?: () => File | null;
  onSend: (args: { prompt: string; displayContent: string; file?: File | null }) => Promise<void>;
  buildPayload?: (args: ChatPromptSenderArgs) => ChatPromptPayload;
  allowEmpty?: boolean;
  trimPrompt?: boolean;
  resetPrompt?: () => void;
  clearAttachment?: () => void;
  resetPromptOnSend?: boolean;
  clearAttachmentOnSend?: boolean;
  shouldPreventSend?: () => boolean;
}

export interface ChatPromptSenderState {
  isSending: boolean;
  sendPrompt: () => Promise<void>;
}

const defaultBuildPayload = ({ prompt }: ChatPromptSenderArgs): ChatPromptPayload => ({
  prompt,
  displayContent: prompt,
});

export const useChatPromptSender = ({
  getPrompt,
  getRawPrompt,
  getAttachment,
  onSend,
  buildPayload = defaultBuildPayload,
  allowEmpty = false,
  trimPrompt = true,
  resetPrompt,
  clearAttachment,
  resetPromptOnSend = true,
  clearAttachmentOnSend = true,
  shouldPreventSend,
}: UseChatPromptSenderOptions): ChatPromptSenderState => {
  const [isSending, setIsSending] = useState(false);

  const sendPrompt = useCallback(async () => {
    if (isSending) {
      return;
    }

    if (shouldPreventSend?.()) {
      return;
    }

    const rawPrompt = getRawPrompt?.() ?? getPrompt();
    const trimmedPrompt = trimPrompt ? rawPrompt.trim() : rawPrompt;
    const file = getAttachment?.() ?? null;

    if (!allowEmpty && !trimmedPrompt && !file) {
      return;
    }

    const payload = buildPayload({ prompt: trimmedPrompt, rawPrompt, file });

    try {
      setIsSending(true);
      await onSend({ ...payload, file });

      if (resetPromptOnSend) {
        resetPrompt?.();
      }

      if (clearAttachmentOnSend) {
        clearAttachment?.();
      }
    } finally {
      setIsSending(false);
    }
  }, [
    allowEmpty,
    buildPayload,
    clearAttachment,
    clearAttachmentOnSend,
    getAttachment,
    getPrompt,
    getRawPrompt,
    isSending,
    onSend,
    resetPrompt,
    resetPromptOnSend,
    shouldPreventSend,
    trimPrompt,
  ]);

  return {
    isSending,
    sendPrompt,
  };
};
